import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PaymentService } from './services/payment/payment.service';
import { PaymentRequest } from './models/PaymentRequest';
import { UserInfoService } from 'src/app/shared/services/UserInfoService/user-info.service';
import { AuthService } from 'src/app/pages/auth/services/auth/auth.service';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.css'],
})
export class PaymentComponent implements OnInit {
  product: any;
  isNewUser: boolean = false;
  isLoading: boolean = false;
  errorMessage: string = '';
  productKey: string = '';
  showEmailExistsModal: boolean = false;

  paymentForm: FormGroup = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    phone: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
    email: ['', [Validators.email]],
    password: [''],
  });

  paymentRequest: PaymentRequest = {
    serviceName: '',
    currency: 'EGP',
    payment_methods: [5016950, 5016951, 5016952],
    items: [],
    billing_data: {
      first_name: '',
      last_name: '',
      phone_number: '',
      email: '',
    },
    customer: { first_name: '', last_name: '', email: '' },
  };

  constructor(
    private fb: FormBuilder,
    private paymentService: PaymentService,
    private router: Router,
    private userInfoService: UserInfoService,
    private authService: AuthService,
  ) {
    const navigate = this.router.getCurrentNavigation();

    if (navigate?.extras.state) {
      this.productKey = navigate.extras.state['productKey'] ?? '';
      this.isNewUser = navigate.extras.state['isNewUser'] ?? false;
      sessionStorage.setItem('productKey', this.productKey);
      sessionStorage.setItem('isNewUser', JSON.stringify(this.isNewUser));
    } else {
      this.productKey = sessionStorage.getItem('productKey') ?? '';
      this.isNewUser = JSON.parse(sessionStorage.getItem('isNewUser') ?? 'false');
    }

    if (!this.productKey) {
      this.router.navigate(['/ar']);
      return;
    }

    this.paymentService
      .isProductsAvailable()
      .subscribe((isAvailable) =>
        !isAvailable ? this.paymentService.setProducts() : null,
      );

    this.getProduct(this.productKey);
  }

  ngOnInit() {
    if (this.isNewUser) {
      this.paymentForm.get('password')?.setValidators([
        Validators.required,
        Validators.minLength(8),
      ]);
      this.paymentForm.get('password')?.updateValueAndValidity();

    } else {
      const userInfo = this.userInfoService.getUserInfo();
      if (userInfo) {
        const nameParts = (userInfo.name || '').split(' ');
        this.paymentForm.patchValue({
          firstName: userInfo.firstName || nameParts[0] || '',
          lastName: userInfo.lastName || nameParts.slice(1).join(' ') || '',
          email: userInfo.email || '',
          phone: userInfo.phone || '',
        });
      }

      this.paymentForm.get('firstName')?.disable();
      this.paymentForm.get('lastName')?.disable();

      const hasEmail = !!(userInfo?.email);
      if (hasEmail) {
        this.paymentForm.get('email')?.disable();
      }

      const isGoogleUser = userInfo?.role === 'GOOGLE';
      const hasPhone = !!(userInfo?.phone);

      if (isGoogleUser && !hasPhone) {
        this.paymentForm.get('phone')?.setValidators([
          Validators.required,
          Validators.pattern('^[0-9]+$'),
        ]);
        this.paymentForm.get('phone')?.updateValueAndValidity();
      } else {
        this.paymentForm.get('phone')?.disable();
      }
    }
  }

  get isGoogleUserMissingPhone(): boolean {
    if (this.isNewUser) return false;
    const userInfo = this.userInfoService.getUserInfo();
    return userInfo?.role === 'GOOGLE' && !userInfo?.phone;
  }

  get firstNameError(): string {
    const ctrl = this.paymentForm.get('firstName');
    if (!ctrl?.touched || !ctrl.errors) return '';
    if (ctrl.errors['required']) return 'الاسم الأول مطلوب';
    return '';
  }

  get lastNameError(): string {
    const ctrl = this.paymentForm.get('lastName');
    if (!ctrl?.touched || !ctrl.errors) return '';
    if (ctrl.errors['required']) return 'اسم العائلة مطلوب';
    return '';
  }

  get phoneError(): string {
    const ctrl = this.paymentForm.get('phone');
    if (!ctrl?.touched || !ctrl.errors) return '';
    if (ctrl.errors['required']) return 'رقم الهاتف مطلوب';
    if (ctrl.errors['pattern']) return 'رقم الهاتف يجب أن يحتوي على أرقام فقط';
    return '';
  }

  get emailError(): string {
    const ctrl = this.paymentForm.get('email');
    if (!ctrl?.touched || !ctrl.errors) return '';
    if (ctrl.errors['email']) return 'صيغة البريد الإلكتروني غير صحيحة';
    return '';
  }

  get passwordError(): string {
    const ctrl = this.paymentForm.get('password');
    if (!ctrl?.touched || !ctrl.errors) return '';
    if (ctrl.errors['required']) return 'كلمة المرور مطلوبة';
    if (ctrl.errors['minlength']) return 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
    return '';
  }

  getProduct(productKey: string) {
    if (!productKey) return;
    this.paymentService.getProduct(productKey).then((product: any) => {
      this.product = product;
      product.quantity = 1;
      this.paymentRequest.items.push(product);
      this.paymentRequest.serviceName = `${productKey}`;
    }).catch((err) => {
      console.error('Failed to get product:', err);
      this.errorMessage = 'حدث خطأ أثناء تحميل بيانات المنتج.';
    });
  }

  private buildPaymentRequest() {
    const form = this.paymentForm.getRawValue();
    this.paymentRequest.serviceName = `${this.productKey}-course`;
    this.paymentRequest.billing_data.first_name = form.firstName;
    this.paymentRequest.billing_data.last_name = form.lastName;
    this.paymentRequest.billing_data.phone_number = form.phone;
    this.paymentRequest.billing_data.email = form.email || 'noemail@placeholder.com';
    this.paymentRequest.customer.first_name = form.firstName;
    this.paymentRequest.customer.last_name = form.lastName;
    this.paymentRequest.customer.email = form.email || 'noemail@placeholder.com';
  }

  onSubmit() {
    if (this.paymentForm.invalid) return;
    this.errorMessage = '';
    this.buildPaymentRequest();

    if (this.isNewUser) {
      this.isLoading = true;
      const form = this.paymentForm.getRawValue();

      const newUser = {
        name: `${form.firstName} ${form.lastName}`,
        phone: form.phone,
        email: form.email || null,
        sub: form.password,
      };

      this.authService.registerUser(newUser).subscribe({
        next: (response: any) => {
          this.userInfoService.setUserInfo(response);
          this.sendPayment();
        },
        error: (err: any) => {
          this.isLoading = false;
          const status = err?.status;
          const msg = err?.error?.message || '';
          const isPhoneTaken =
            status === 403 ||
            msg.toLowerCase().includes('phone') ||
            msg.toLowerCase().includes('already');

          if (isPhoneTaken) {
            this.showEmailExistsModal = true;
          } else {
            this.errorMessage = 'حدث خطأ أثناء إنشاء الحساب. حاول مرة أخرى.';
          }
        },
      });
    } else {
      this.sendPayment();
    }
  }

  closeModal() { this.showEmailExistsModal = false; }

  goToLogin() {
    this.showEmailExistsModal = false;
    this.router.navigate(['/ar/login'], {
      state: { redirectTo: '/ar/payment', productKey: this.productKey }
    });
  }

  private sendPayment() {
    this.isLoading = true;
    const productName = this.getProductName(this.productKey);

    if (typeof (window as any).fbq !== 'undefined') {
      (window as any).fbq('track', 'InitiateCheckout', {
        content_ids: [this.productKey],
        content_name: productName,
        content_type: 'product',
      });

    } 

    if (typeof (window as any).ttq !== 'undefined') {
      (window as any).ttq.track('InitiateCheckout', {
        content_id: this.productKey,
        content_name: productName,
        content_type: 'product',
      });
    
    } 

    this.paymentService.sendPaymentRequest(this.paymentRequest).subscribe({
      next: (paymentLink: any) => {
        this.isLoading = false;
        sessionStorage.removeItem('productKey');
        sessionStorage.removeItem('isNewUser');
        window.open(paymentLink.replace(' ', ''), '_blank');
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'حدث خطأ أثناء إنشاء رابط الدفع. حاول مرة أخرى.';
      },
    });
  }

  private getProductName(productKey: string): string {
    const productNames: { [key: string]: string } = {
      'grammar': 'كورس النحو',
      'dictation': 'كورس الاملاء',
      'grammar-dictation': 'كورس النحو والاملاء',
    };
    return productNames[productKey] ?? productKey;
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    return /[0-9]/.test(event.key);
  }
}